import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import {
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Settings,
  Trash2,
  Calendar,
  Zap,
  AlertCircle,
  Plus,
  ExternalLink,
  Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";

const TIER_LABELS = { free: "Free", vip: "VIP" };

// Build a t.me URL from the channel info. Preference order:
// 1) Real invite link (works for non-members)
// 2) Public @username → t.me/{username}
// 3) Fallback: raw channelId (will only work for already-members on private channels)
const buildTelegramUrl = (row) => {
  if (!row) return null;
  if (row.inviteLink && /^https?:\/\//.test(row.inviteLink)) return row.inviteLink;
  if (row.channelUsername) return `https://t.me/${String(row.channelUsername).replace(/^@/, "")}`;
  const v = String(row.channelId || "").trim();
  if (!v) return null;
  if (v.startsWith("http://") || v.startsWith("https://")) return v;
  if (v.startsWith("@")) return `https://t.me/${v.slice(1)}`;
  if (v.startsWith("-100")) return `https://t.me/c/${v.slice(4)}`;
  if (/^-?\d+$/.test(v)) return null;
  return `https://t.me/${v}`;
};

const StatusPill = ({ row }) => {
  if (!row.configured)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-white/5 text-white/50 border border-white/10">
        <Plus className="w-3 h-3" /> Sin configurar
      </span>
    );
  if (row.canPost === true)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-green-500/20 text-green-300 border border-green-500/30">
        <CheckCircle2 className="w-3 h-3" /> Listo
      </span>
    );
  if (row.canPost === false && row.isAdmin === true)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-200 border border-amber-500/30">
        <AlertCircle className="w-3 h-3" /> Sin permiso
      </span>
    );
  if (row.isAdmin === false)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-red-500/20 text-red-300 border border-red-500/30">
        <XCircle className="w-3 h-3" /> Bot no admin
      </span>
    );
  if (row.verifyError)
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-red-500/20 text-red-300 border border-red-500/30"
        title={row.verifyError}
      >
        <XCircle className="w-3 h-3" /> Error
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-300 border border-blue-500/30">
      <Clock className="w-3 h-3" /> Vinculado
    </span>
  );
};

/* Inline quick-link form: paste link → save + verify in one call */
const QuickLinkForm = ({ row, getAuthHeaders, onSaved, botUsername }) => {
  const [val, setVal] = useState(row.channelId || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!val.trim()) {
      toast.error("Pega el link o @username del canal");
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(
        `/api/admin/autopost/${row.creatorUsername}/schedule/quick-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({
            tier: row.tier,
            channelId: val.trim(),
            enabled: true,
          }),
        }
      );
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error al vincular");
        return;
      }
      if (d.verify?.ok && d.verify?.canPost) {
        toast.success(`✓ Conectado: ${d.verify.title || row.tier}`);
      } else if (d.verify?.ok && !d.verify?.canPost) {
        toast.warning(
          d.verify.isAdmin
            ? "Vinculado, pero falta permiso 'Publicar mensajes'"
            : "Vinculado, pero el bot aún no es admin del canal"
        );
      } else {
        toast.warning(
          "Guardado, pero no se pudo verificar: " +
            (d.verify?.error || "desconocido")
        );
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error("Error de red");
    } finally {
      setSaving(false);
    }
  };

  const addBotUrl = botUsername
    ? `https://t.me/${botUsername}?startchannel=true&admin=post_messages+edit_messages+delete_messages`
    : null;

  return (
    <div className="mt-2 flex flex-wrap gap-2 items-center">
      <input
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="https://t.me/micanal o @micanal"
        data-testid={`quicklink-input-${row.tier}-${row.creatorUsername}`}
        className="flex-1 min-w-[200px] bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs outline-none focus:border-white/30"
      />
      <button
        onClick={save}
        disabled={saving}
        data-testid={`quicklink-save-${row.tier}-${row.creatorUsername}`}
        className="px-3 py-1.5 bg-white text-black font-semibold rounded-lg text-xs disabled:opacity-50 flex items-center gap-1"
      >
        <LinkIcon className="w-3 h-3" />
        {saving ? "..." : "Vincular"}
      </button>
      {addBotUrl && (
        <a
          href={addBotUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-200 text-[11px] rounded-lg"
          title="Agregar bot al canal"
        >
          + Bot
        </a>
      )}
    </div>
  );
};

const ChannelRow = ({ row, getAuthHeaders, onChange, botUsername }) => {
  const [editing, setEditing] = useState(!row.configured);
  const [sending, setSending] = useState(false);

  const onDelete = async () => {
    if (
      !window.confirm(
        `¿Desvincular el canal ${row.tier.toUpperCase()} de @${row.creatorUsername}?`
      )
    )
      return;
    const r = await fetch(
      `/api/admin/autopost/${row.creatorUsername}/schedule/${row.tier}`,
      { method: "DELETE", headers: { ...getAuthHeaders() } }
    );
    if (r.ok) {
      toast.success("Desvinculado");
      onChange && onChange();
    } else {
      toast.error("Error al desvincular");
    }
  };

  const sendNow = async () => {
    if (
      !window.confirm(
        `¿Enviar 1 post AHORA al canal ${row.tier.toUpperCase()} de @${row.creatorUsername}?`
      )
    )
      return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("tier", row.tier);
      const r = await fetch(
        `/api/admin/autopost/${row.creatorUsername}/test`,
        { method: "POST", headers: { ...getAuthHeaders() }, body: fd }
      );
      if (r.ok) {
        toast.success(`✅ Enviado a @${row.creatorUsername} ${row.tier.toUpperCase()}`);
        onChange && onChange();
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || "Error al enviar", { duration: 8000 });
      }
    } catch (e) {
      toast.error("Error de red");
    } finally {
      setSending(false);
    }
  };

  const toggleEnabled = async () => {
    const r = await fetch(
      `/api/admin/autopost/${row.creatorUsername}/schedule/${row.tier}/toggle`,
      { method: "PATCH", headers: { ...getAuthHeaders() } }
    );
    if (r.ok) {
      const d = await r.json();
      toast.success(
        d.enabled
          ? `✅ Activado: ${row.creatorUsername} ${row.tier.toUpperCase()}`
          : `⏸️ Pausado: ${row.creatorUsername} ${row.tier.toUpperCase()} (no recibirá masivos)`
      );
      onChange && onChange();
    } else {
      toast.error("Error al cambiar estado");
    }
  };

  return (
    <div
      className="p-3 bg-white/5 border border-white/10 rounded-lg hover:border-white/20 transition-colors"
      data-testid={`channel-row-${row.tier}-${row.creatorUsername}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-medium text-sm">
              @{row.creatorUsername}
            </span>
            {row.creatorName && row.creatorName !== row.creatorUsername && (
              <span className="text-white/40 text-[11px]">· {row.creatorName}</span>
            )}
            <StatusPill row={row} />
            {row.configured && (
              <button
                onClick={toggleEnabled}
                data-testid={`channel-toggle-${row.tier}-${row.creatorUsername}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold transition-colors ${
                  row.enabled
                    ? "bg-green-500/20 text-green-300 border border-green-500/30 hover:bg-green-500/30"
                    : "bg-white/5 text-white/40 border border-white/10 hover:bg-white/10"
                }`}
                title={
                  row.enabled
                    ? "Click para pausar (excluir de broadcasts masivos)"
                    : "Click para activar"
                }
              >
                {row.enabled ? "● Activo" : "⏸ Pausado"}
              </button>
            )}
          </div>
          {row.configured && !editing && (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-white/60 text-xs font-mono truncate">
                {row.channelId}
              </span>
              {row.title && (
                <span className="text-white/40 text-[11px]">· {row.title}</span>
              )}
              {row.memberCount != null && (
                <span className="text-white/40 text-[11px]">
                  · {row.memberCount} miembros
                </span>
              )}
            </div>
          )}
          {row.configured && row.hours?.length > 0 && !editing && (
            <p className="text-[10px] text-white/40 mt-1 truncate">
              Horarios: {row.hours.join(", ")}
            </p>
          )}
          {editing && (
            <QuickLinkForm
              row={row}
              getAuthHeaders={getAuthHeaders}
              botUsername={botUsername}
              onSaved={() => {
                setEditing(false);
                onChange && onChange();
              }}
            />
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          {row.configured && (() => {
            const url = buildTelegramUrl(row);
            return url ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`channel-open-${row.tier}-${row.creatorUsername}`}
                className="p-1.5 text-sky-300 hover:text-sky-200 rounded hover:bg-sky-500/10"
                title="Abrir canal en Telegram"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            ) : null;
          })()}
          {row.configured && (
            <button
              onClick={sendNow}
              disabled={sending}
              data-testid={`channel-sendnow-${row.tier}-${row.creatorUsername}`}
              className="p-1.5 text-green-300 hover:text-green-200 rounded hover:bg-green-500/10 disabled:opacity-50"
              title="Enviar 1 post ahora a este canal"
            >
              {sending ? (
                <div className="w-4 h-4 border-2 border-green-300/30 border-t-green-300 rounded-full animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          )}
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              className="p-1.5 text-white/50 hover:text-white rounded hover:bg-white/10"
              title="Editar link"
              data-testid={`channel-edit-${row.tier}-${row.creatorUsername}`}
            >
              <LinkIcon className="w-4 h-4" />
            </button>
          ) : (
            row.configured && (
              <button
                onClick={() => setEditing(false)}
                className="p-1.5 text-white/50 hover:text-white rounded hover:bg-white/10"
                title="Cancelar"
              >
                <XCircle className="w-4 h-4" />
              </button>
            )
          )}
          <Link
            to={`/admin/creadoras/${row.creatorUsername}/auto-posting`}
            className="p-1.5 text-white/50 hover:text-white rounded hover:bg-white/10"
            title="Configuración avanzada"
            data-testid={`channel-configure-${row.tier}-${row.creatorUsername}`}
          >
            <Settings className="w-4 h-4" />
          </Link>
          {row.configured && (
            <button
              onClick={onDelete}
              data-testid={`channel-delete-${row.tier}-${row.creatorUsername}`}
              className="p-1.5 text-red-300/70 hover:text-red-300 rounded hover:bg-red-500/10"
              title="Desvincular"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ChannelTable = ({ rows, tier, getAuthHeaders, onChange, botUsername }) => {
  const [showAll, setShowAll] = useState(false);
  if (rows.length === 0) {
    return (
      <p className="text-white/40 text-sm text-center py-12">
        No hay modelos activas todavía.
      </p>
    );
  }
  const configured = rows.filter((r) => r.configured);
  const unconfigured = rows.filter((r) => !r.configured);
  const visible = showAll ? rows : [...configured, ...unconfigured.slice(0, 3)];

  return (
    <div className="space-y-2">
      {visible.map((r) => (
        <ChannelRow
          key={`${r.creatorUsername}-${r.tier}`}
          row={r}
          getAuthHeaders={getAuthHeaders}
          onChange={onChange}
          botUsername={botUsername}
        />
      ))}
      {!showAll && unconfigured.length > 3 && (
        <button
          onClick={() => setShowAll(true)}
          className="w-full py-2 text-white/60 hover:text-white text-xs bg-white/5 hover:bg-white/10 rounded-lg"
        >
          Mostrar {unconfigured.length - 3} modelos sin configurar más ↓
        </button>
      )}
      {showAll && (
        <button
          onClick={() => setShowAll(false)}
          className="w-full py-2 text-white/60 hover:text-white text-xs bg-white/5 hover:bg-white/10 rounded-lg"
        >
          Mostrar menos ↑
        </button>
      )}
    </div>
  );
};

const BroadcastBox = ({ tier, channels, getAuthHeaders, onDone }) => {
  const [scheduleMode, setScheduleMode] = useState("now"); // now | schedule
  const [scheduleAt, setScheduleAt] = useState("");
  const [sending, setSending] = useState(false);

  const ready = channels.filter((c) => c.configured && c.channelId && c.enabled);
  const pausedCount = channels.filter((c) => c.configured && c.channelId && !c.enabled).length;
  const tierLabel = TIER_LABELS[tier];
  const colorClass =
    tier === "free"
      ? "from-blue-500 to-cyan-500"
      : "from-amber-500 to-orange-500";

  const send = async () => {
    if (ready.length === 0) {
      toast.error(`No hay canales ${tierLabel} vinculados`);
      return;
    }
    if (scheduleMode === "schedule" && !scheduleAt) {
      toast.error("Elige fecha y hora");
      return;
    }
    if (scheduleMode === "now") {
      if (
        !window.confirm(
          `¿Enviar 1 publicación AHORA a los ${ready.length} canales ${tierLabel}? Esto consumirá 1 media de cada modelo.`
        )
      )
        return;
    }
    setSending(true);
    try {
      const r = await fetch(`/api/admin/autopost/broadcast`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          tier,
          scheduleAt: scheduleMode === "schedule" ? scheduleAt : null,
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error en broadcast");
        return;
      }
      const skipped = (d.skipped || []).length;
      if (scheduleMode === "now") {
        toast.success(
          `📤 Enviados: ${d.sent}/${d.totalChannels}` +
            (skipped > 0 ? ` · ${skipped} omitidos` : "")
        );
      } else {
        toast.success(
          `🗓️ Programados: ${d.scheduled}/${d.totalChannels}` +
            (skipped > 0 ? ` · ${skipped} omitidos` : "")
        );
      }
      if (d.skipped?.length) {
        const sample = d.skipped
          .slice(0, 3)
          .map((s) => `• @${s.username}: ${s.reason}`)
          .join("\n");
        toast.message("Detalles", { description: sample, duration: 8000 });
      }
      onDone && onDone();
    } catch (e) {
      toast.error("Error de red");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={`bg-gradient-to-br ${colorClass} bg-opacity-10 border border-white/10 rounded-xl p-4`}
      data-testid={`broadcast-box-${tier}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorClass} flex items-center justify-center`}
        >
          <Send className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className="text-white font-bold text-sm">
            Broadcast {tierLabel}
          </h3>
          <p className="text-white/60 text-[11px]">
            {ready.length} canales activos
            {pausedCount > 0 && (
              <span className="text-white/40"> · {pausedCount} pausados</span>
            )}
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setScheduleMode("now")}
          data-testid={`broadcast-${tier}-now`}
          className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold ${
            scheduleMode === "now"
              ? "bg-white text-black"
              : "bg-white/10 text-white/70"
          }`}
        >
          <Zap className="w-3.5 h-3.5 inline mr-1" />
          Enviar ahora
        </button>
        <button
          onClick={() => setScheduleMode("schedule")}
          data-testid={`broadcast-${tier}-schedule`}
          className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold ${
            scheduleMode === "schedule"
              ? "bg-white text-black"
              : "bg-white/10 text-white/70"
          }`}
        >
          <Calendar className="w-3.5 h-3.5 inline mr-1" />
          Programar
        </button>
      </div>

      {scheduleMode === "schedule" && (
        <input
          type="datetime-local"
          value={scheduleAt}
          onChange={(e) => setScheduleAt(e.target.value)}
          data-testid={`broadcast-${tier}-datetime`}
          className="w-full mb-3 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/30"
        />
      )}

      <button
        onClick={send}
        disabled={sending || ready.length === 0}
        data-testid={`broadcast-${tier}-send`}
        className="w-full py-2.5 bg-white text-black font-bold rounded-lg text-sm hover:bg-white/90 disabled:opacity-50"
      >
        {sending
          ? "Enviando..."
          : scheduleMode === "now"
          ? `Enviar a ${ready.length} canales ${tierLabel}`
          : `Programar broadcast ${tierLabel}`}
      </button>
    </div>
  );
};

const DailyBroadcastConfig = ({ getAuthHeaders, onChange }) => {
  const [schedules, setSchedules] = useState({ free: null, vip: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/admin/autopost/broadcast-schedule`, {
        headers: { ...getAuthHeaders() },
      });
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        setLoading(false);
        return;
      }
      const d = await r.json();
      setSchedules(d.schedules || {});
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const saveTier = async (tier, postsPerDay, enabled) => {
    setSaving((s) => ({ ...s, [tier]: true }));
    try {
      const r = await fetch(`/api/admin/autopost/broadcast-schedule`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ tier, postsPerDay, enabled }),
      });
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        if (r.status === 404) {
          toast.error(
            "Esta función todavía no está deployada en este entorno. Haz Deploy en Emergent.",
            { duration: 8000 }
          );
        } else {
          toast.error(`Respuesta inesperada (HTTP ${r.status})`, { duration: 6000 });
        }
        return;
      }
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error al guardar");
        return;
      }
      setSchedules((s) => ({ ...s, [tier]: d }));
      toast.success(
        d.enabled
          ? `✅ ${tier.toUpperCase()}: ${d.postsPerDay} broadcasts/día activos`
          : `⏸️ ${tier.toUpperCase()} pausado`
      );
      onChange && onChange();
    } catch (e) {
      toast.error(`Error de red: ${e?.message || "desconocido"}`, { duration: 6000 });
    } finally {
      setSaving((s) => ({ ...s, [tier]: false }));
    }
  };

  if (loading) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-4">
      <div className="mb-3">
        <h2 className="text-white font-bold text-base flex items-center gap-2">
          ⚡ Broadcast diario automático
        </h2>
        <p className="text-purple-200/70 text-xs mt-0.5">
          Decide cuántos masivos enviar al día — el sistema reparte los
          horarios solo y publica a todos los canales activos.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {["free", "vip"].map((tier) => (
          <DailyBroadcastTierRow
            key={tier}
            tier={tier}
            sched={schedules[tier]}
            saving={saving[tier]}
            onSave={(n, en) => saveTier(tier, n, en)}
          />
        ))}
      </div>
    </div>
  );
};

const DailyBroadcastTierRow = ({ tier, sched, saving, onSave }) => {
  const [n, setN] = useState((sched?.postsPerDay ?? 0).toString());
  useEffect(() => {
    setN((sched?.postsPerDay ?? 0).toString());
  }, [sched?.postsPerDay]);
  const enabled = !!sched?.enabled;
  const hours = sched?.hours || [];
  const tierLabel = tier === "free" ? "Free" : "VIP";
  const colorClass =
    tier === "free"
      ? "from-blue-500/20 to-cyan-500/10 border-blue-500/30"
      : "from-amber-500/20 to-orange-500/10 border-amber-500/30";
  const tierColor = tier === "free" ? "text-blue-200" : "text-amber-200";

  return (
    <div className={`bg-gradient-to-br ${colorClass} border rounded-lg p-3 space-y-2`}>
      <div className="flex items-center justify-between">
        <span className={`text-[11px] font-bold uppercase ${tierColor}`}>
          {tierLabel}
        </span>
        <button
          onClick={() => onSave(parseInt(n) || 0, !enabled)}
          disabled={saving}
          data-testid={`daily-toggle-${tier}`}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${
            enabled
              ? "bg-green-500/30 text-green-200 border border-green-500/40"
              : "bg-white/10 text-white/50 border border-white/10"
          }`}
        >
          {enabled ? "● Activo" : "⏸ Pausado"}
        </button>
      </div>
      <div className="flex gap-2 items-center">
        <input
          type="number"
          min="0"
          max="48"
          value={n}
          onChange={(e) => setN(e.target.value)}
          data-testid={`daily-n-${tier}`}
          placeholder="0"
          className="w-20 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-white text-sm outline-none focus:border-white/30"
        />
        <span className="text-white/60 text-xs">broadcasts / día</span>
        <button
          onClick={() => onSave(parseInt(n) || 0, parseInt(n) > 0)}
          disabled={saving}
          data-testid={`daily-save-${tier}`}
          className="ml-auto px-3 py-1.5 bg-white text-black text-xs font-bold rounded-lg disabled:opacity-50"
        >
          {saving ? "..." : "Guardar"}
        </button>
      </div>
      {hours.length > 0 && (
        <div>
          <p className="text-[10px] text-white/50 mb-1">
            Horarios automáticos (zona Bogotá):
          </p>
          <div className="flex flex-wrap gap-1">
            {hours.map((h) => (
              <span
                key={h}
                className="text-[10px] font-mono bg-black/30 border border-white/10 rounded px-1.5 py-0.5 text-white/80"
              >
                {h}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* Modal: discover channels where the bot was added (from webhook events) */
const DiscoverChannelsModal = ({ open, onClose, getAuthHeaders, creators, onLinked }) => {
  const [data, setData] = useState({ channels: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/admin/autopost/discover-channels`, {
        headers: { ...getAuthHeaders() },
      });
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        toast.error("Función no disponible aún en este entorno. Haz Deploy.");
        return;
      }
      const d = await r.json();
      if (!d.ok) {
        toast.error(d.error || "No se pudieron obtener los canales");
        return;
      }
      setData(d);
    } catch (e) {
      toast.error("Error de red");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center pt-16 p-4"
      onClick={onClose}
    >
      <div
        className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-white/10 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-white font-bold text-lg">🔍 Canales detectados</h2>
            <p className="text-white/60 text-xs mt-0.5">
              Aquí aparecen los canales donde @Jglinksbot fue agregado como
              admin. Pulsa "Vincular" y elige a qué modelo.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-white/50 hover:text-white"
            data-testid="discover-close"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading && (
            <div className="py-12 flex justify-center">
              <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            </div>
          )}
          {!loading && data.channels.length === 0 && (
            <div className="text-center py-10">
              <p className="text-white/70 text-sm mb-3">
                No detecté ningún canal todavía.
              </p>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-left">
                <p className="text-blue-200 text-xs font-semibold mb-2">
                  Cómo hacer que aparezca aquí:
                </p>
                <ol className="text-white/80 text-xs space-y-1.5 list-decimal pl-5">
                  <li>En Telegram, abre tu canal</li>
                  <li>
                    Ve a <strong>Administradores</strong> → <strong>Agregar admin</strong>
                  </li>
                  <li>
                    Busca <span className="font-mono bg-white/10 px-1 rounded">@Jglinksbot</span>{" "}
                    y agrégalo
                  </li>
                  <li>
                    Activa el permiso <strong>Publicar mensajes</strong> y guarda
                  </li>
                  <li>Vuelve aquí y pulsa "Refrescar"</li>
                </ol>
              </div>
              <button
                onClick={load}
                className="mt-4 px-4 py-2 bg-white text-black text-xs font-bold rounded-lg"
              >
                Refrescar
              </button>
            </div>
          )}
          {!loading &&
            data.channels.map((c) => (
              <DiscoveredChannelRow
                key={c.id}
                channel={c}
                creators={creators}
                getAuthHeaders={getAuthHeaders}
                onLinked={() => {
                  onLinked && onLinked();
                  load();
                }}
              />
            ))}
        </div>
        {!loading && data.channels.length > 0 && (
          <div className="p-3 border-t border-white/10 flex justify-end">
            <button
              onClick={load}
              className="px-3 py-1.5 bg-white/10 hover:bg-white/15 text-white text-xs rounded-lg"
            >
              Refrescar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const DiscoveredChannelRow = ({ channel, creators, getAuthHeaders, onLinked }) => {
  const [picking, setPicking] = useState(false);
  const [creator, setCreator] = useState("");
  const [tier, setTier] = useState("vip");
  const [linking, setLinking] = useState(false);

  const link = async () => {
    if (!creator) {
      toast.error("Elige una modelo");
      return;
    }
    setLinking(true);
    try {
      const r = await fetch(
        `/api/admin/autopost/${creator}/schedule/quick-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({
            tier,
            channelId: channel.id,
            enabled: true,
          }),
        }
      );
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Error al vincular");
        return;
      }
      toast.success(`✅ Vinculado a @${creator} ${tier.toUpperCase()}`);
      setPicking(false);
      onLinked && onLinked();
    } catch (e) {
      toast.error("Error de red");
    } finally {
      setLinking(false);
    }
  };

  const isLinked = channel.linked?.length > 0;
  const isPublic = !!channel.username;
  const canPost = channel.canPost === true;

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-3" data-testid={`discovered-${channel.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-semibold text-sm truncate">
              {channel.title || "(sin título)"}
            </span>
            {isPublic ? (
              <span className="text-[10px] bg-green-500/20 text-green-300 border border-green-500/30 px-1.5 py-0.5 rounded">
                Público @{channel.username}
              </span>
            ) : (
              <span className="text-[10px] bg-amber-500/20 text-amber-200 border border-amber-500/30 px-1.5 py-0.5 rounded">
                Privado
              </span>
            )}
            {canPost && (
              <span className="text-[10px] bg-green-500/20 text-green-300 px-1.5 py-0.5 rounded">
                ✓ Puede publicar
              </span>
            )}
            {channel.canPost === false && (
              <span className="text-[10px] bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded">
                ✗ Sin permiso
              </span>
            )}
          </div>
          <p className="text-white/40 text-[11px] font-mono mt-0.5">
            ID: {channel.id}
            {channel.memberCount != null && ` · ${channel.memberCount} miembros`}
          </p>
          {isLinked && (
            <div className="mt-1 flex flex-wrap gap-1">
              {channel.linked.map((l) => (
                <span
                  key={`${l.creatorUsername}-${l.tier}`}
                  className="text-[10px] bg-blue-500/20 text-blue-300 border border-blue-500/30 px-1.5 py-0.5 rounded"
                >
                  @{l.creatorUsername} · {l.tier.toUpperCase()}
                </span>
              ))}
            </div>
          )}
        </div>
        {!picking && (
          <button
            onClick={() => setPicking(true)}
            data-testid={`link-channel-${channel.id}`}
            className="px-3 py-1.5 bg-white text-black text-xs font-bold rounded-lg flex-shrink-0"
          >
            Vincular
          </button>
        )}
      </div>
      {picking && (
        <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-2 items-center">
          <select
            value={creator}
            onChange={(e) => setCreator(e.target.value)}
            data-testid={`pick-creator-${channel.id}`}
            className="bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs"
          >
            <option value="">— Modelo —</option>
            {creators.map((u) => (
              <option key={u} value={u}>
                @{u}
              </option>
            ))}
          </select>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            className="bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs"
          >
            <option value="free">Free</option>
            <option value="vip">VIP</option>
          </select>
          <button
            onClick={link}
            disabled={linking || !creator}
            className="ml-auto px-3 py-1.5 bg-white text-black text-xs font-bold rounded-lg disabled:opacity-50"
          >
            {linking ? "..." : "Confirmar"}
          </button>
          <button
            onClick={() => setPicking(false)}
            className="px-2 py-1.5 text-white/50 hover:text-white text-xs"
          >
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
};

const AdminAutoPostChannels = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState({ free: [], vip: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [verifyStatus, setVerifyStatus] = useState(false);
  const [botUsername, setBotUsername] = useState("");
  const [discoverOpen, setDiscoverOpen] = useState(false);

  // Distinct creator usernames for the discover modal picker
  const creators = Array.from(
    new Set([...(data.free || []), ...(data.vip || [])].map((r) => r.creatorUsername))
  ).sort();

  const cancelAllPending = async () => {
    if (
      !window.confirm(
        "⚠️ EMERGENCIA: ¿Cancelar TODOS los posts pendientes en cola?\n\nEsto detiene cualquier envío que esté por dispararse. Los ya enviados no se afectan."
      )
    )
      return;
    try {
      const r = await fetch(`/api/admin/autopost/queue/clear-pending`, {
        method: "POST",
        headers: { ...getAuthHeaders() },
      });
      const ct = r.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        toast.error("Función no disponible aún en este entorno. Haz Deploy.");
        return;
      }
      const d = await r.json();
      toast.success(`🛑 ${d.cancelled} posts cancelados`);
    } catch (e) {
      toast.error("Error de red");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [chRes, botRes] = await Promise.all([
        fetch(
          `/api/admin/autopost/channels${verifyStatus ? "?verify_status=true" : ""}`,
          { headers: { ...getAuthHeaders() } }
        ),
        fetch(`/api/admin/autopost/bot-info`, { headers: { ...getAuthHeaders() } }),
      ]);
      const d = await chRes.json();
      const b = await botRes.json();
      setData(d);
      if (b?.ok) setBotUsername(b.username || "");
    } catch (e) {
      toast.error("Error al cargar canales");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, verifyStatus]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
              Auto-Posting · Canales
            </h1>
            <p className="text-white/60 text-sm">
              Vista global de TODAS las modelos
              {botUsername && (
                <>
                  {" "}
                  · bot <span className="font-mono">@{botUsername}</span>
                </>
              )}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={cancelAllPending}
              data-testid="cancel-all-pending"
              title="Detiene TODO envío pendiente inmediatamente"
              className="px-3 py-2 bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 rounded-lg text-xs font-bold flex items-center gap-1.5"
            >
              🛑 Detener todo
            </button>
            <button
              onClick={() => setDiscoverOpen(true)}
              data-testid="open-discover"
              className="px-3 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-pink-500/30 text-pink-200 hover:bg-pink-500/30 rounded-lg text-xs font-medium flex items-center gap-1.5"
            >
              🔍 Detectar canales
            </button>
            <Link
              to="/admin/auto-posting/broadcast"
              data-testid="link-broadcast"
              className="px-3 py-2 bg-gradient-to-r from-pink-500/20 to-rose-500/20 border border-pink-500/30 text-pink-200 hover:bg-pink-500/30 rounded-lg text-xs font-medium flex items-center gap-1.5"
            >
              📡 Broadcast
            </Link>
            <Link
              to="/admin/auto-posting/cola"
              data-testid="link-queue"
              className="px-3 py-2 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/30 rounded-lg text-xs font-medium flex items-center gap-1.5"
            >
              📊 Cola
            </Link>
            <Link
              to="/admin/auto-posting/diagnostico"
              data-testid="link-diagnostics"
              className="px-3 py-2 bg-gradient-to-r from-red-500/20 to-amber-500/20 border border-amber-500/30 text-amber-200 hover:bg-amber-500/30 rounded-lg text-xs font-medium flex items-center gap-1.5"
            >
              🔎 Diagnóstico
            </Link>
            <button
              onClick={() => setVerifyStatus((v) => !v)}
              data-testid="verify-status-toggle"
              className={`px-3 py-2 rounded-lg text-xs font-medium border ${
                verifyStatus
                  ? "bg-green-500/20 text-green-200 border-green-500/30"
                  : "bg-white/5 text-white/70 border-white/10"
              }`}
            >
              {verifyStatus ? "✓ Verificando estado" : "Verificar estado de bots"}
            </button>
            <button
              onClick={load}
              className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/5"
              data-testid="reload-channels"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
            <div className="text-blue-300 text-xs">Canales Free</div>
            <div className="text-white text-2xl font-bold">
              {data.totals.free || 0}
              <span className="text-white/40 text-sm font-normal">
                {" "}
                / {data.totals.freeTotal || 0}
              </span>
            </div>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
            <div className="text-amber-300 text-xs">Canales VIP</div>
            <div className="text-white text-2xl font-bold">
              {data.totals.vip || 0}
              <span className="text-white/40 text-sm font-normal">
                {" "}
                / {data.totals.vipTotal || 0}
              </span>
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="text-white/60 text-xs">Configurados / Total</div>
            <div className="text-white text-2xl font-bold">
              {data.totals.all || 0}
              <span className="text-white/40 text-sm font-normal">
                {" "}
                / {data.totals.allTotal || 0}
              </span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center">
            <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <DailyBroadcastConfig getAuthHeaders={getAuthHeaders} onChange={load} />
            <div className="grid lg:grid-cols-2 gap-6">
            {/* FREE column */}
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-200 text-[10px] font-bold rounded uppercase">
                  Free
                </span>
                <h2 className="text-white font-semibold">
                  Canales Free ({data.totals.free || 0} / {data.totals.freeTotal || 0})
                </h2>
              </div>
              <BroadcastBox
                tier="free"
                channels={data.free || []}
                getAuthHeaders={getAuthHeaders}
                onDone={load}
              />
              <ChannelTable
                rows={data.free || []}
                tier="free"
                getAuthHeaders={getAuthHeaders}
                onChange={load}
                botUsername={botUsername}
              />
            </section>

            {/* VIP column */}
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-200 text-[10px] font-bold rounded uppercase">
                  VIP
                </span>
                <h2 className="text-white font-semibold">
                  Canales VIP ({data.totals.vip || 0} / {data.totals.vipTotal || 0})
                </h2>
              </div>
              <BroadcastBox
                tier="vip"
                channels={data.vip || []}
                getAuthHeaders={getAuthHeaders}
                onDone={load}
              />
              <ChannelTable
                rows={data.vip || []}
                tier="vip"
                getAuthHeaders={getAuthHeaders}
                onChange={load}
                botUsername={botUsername}
              />
            </section>
          </div>
          </>
        )}
      </div>
      <DiscoverChannelsModal
        open={discoverOpen}
        onClose={() => setDiscoverOpen(false)}
        getAuthHeaders={getAuthHeaders}
        creators={creators}
        onLinked={load}
      />
    </AdminLayout>
  );
};

export default AdminAutoPostChannels;
